import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Megaphone, Building2, BadgeCheck } from "lucide-react";
import { api } from "@/lib/api";
import { AdaptiveMediaFrame } from "@/components/AdaptiveMediaFrame";

const NAVY = "#0F2C59";
const GOLD = "#D4A017";

/* Centralized, reusable advertising surface. Fully isolated from Match/Sales Coach/
   ranking logic — it only reads /ad-campaigns for its placement key. Every paid item
   is visibly labelled via `disclosure`. Impressions are de-duped per campaign id so
   React re-renders never double count. */

const seenImpressions = new Set(); // module-level guard against rerender double-fire

function useAdCampaigns(placement) {
  const [campaigns, setCampaigns] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/ad-campaigns`, { params: { placement } })
      .then((r) => { if (alive) setCampaigns(r.data.campaigns || []); })
      .catch(() => { if (alive) setCampaigns([]); });
    return () => { alive = false; };
  }, [placement]);
  return campaigns;
}

function track(event, c, placement, slotIndex) {
  const key = `${event}:${placement}:${c.id}`;
  if (event === "impression") {
    if (seenImpressions.has(key)) return;
    seenImpressions.add(key);
  }
  api.post("/ads/event", {
    campaign_id: c.id, placement, campaign_type: c.campaign_type || "",
    destination: c.cta_destination || "", disclosure: c.disclosure || "",
    slot_index: slotIndex || null, event,
  }).catch(() => {});
}

function SponsorTag({ text, tone = "gold" }) {
  // PropertyConnect Spotlight disclosure identity: solid GOLD pill + WHITE text on every
  // surface/state (Featured / Sponsored / Partner Offer). Wording/semantics unchanged.
  const styles = { background: GOLD, color: "#fff", border: "1px solid rgba(255,255,255,0.35)" };
  return (
    <span data-testid="ad-sponsor-tag" className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] shadow-sm" style={styles}>
      <Megaphone className="h-3 w-3" /> {text}
    </span>
  );
}

function CTA({ c, placement, className, style, light, slotIndex }) {
  const rawLabel = (c.cta_label || "").trim();
  const label = <>{rawLabel} <ArrowRight className="h-3.5 w-3.5" /></>;
  const onClick = () => track("click", c, placement, slotIndex);
  const dest = (c.cta_destination || "").trim();
  const isHttps = /^https:\/\//i.test(dest);
  const isInternal = dest.startsWith("/") && !dest.startsWith("//");
  const base = "inline-flex items-center gap-1.5 font-semibold rounded-xl active:scale-[0.98] transition-transform";
  if (!dest || !rawLabel || (!isHttps && !isInternal)) return null; // blank label OR unsafe/malformed destination => no CTA
  if (isHttps) {
    return <a data-testid={`ad-cta-${c.id}`} href={dest} target="_blank" rel="noopener noreferrer sponsored" onClick={onClick} className={`${base} ${className}`} style={style}>{label}</a>;
  }
  return <Link data-testid={`ad-cta-${c.id}`} to={dest} onClick={onClick} className={`${base} ${className}`} style={style}>{label}</Link>;
}

/* ---- Variant: HERO (dashboard premium slide, navy canvas) ---- */
export function AdHeroSlide({ campaign, placement }) {
  const c = campaign;
  useEffect(() => { track("impression", c, placement); }, [c, placement]);
  // Sponsored vs Featured disclosure — derived from existing sponsor_kind metadata
  // (SPOTLIGHT = paid campaign → "Sponsored"; FEATURED = PropertyConnect editorial → "Featured").
  const isFeatured = (c.sponsor_kind || "").toUpperCase() === "FEATURED";
  const discLabel = isFeatured ? "Featured" : "Sponsored";
  const isEvent = /event|open\s*house/i.test(c.campaign_type || "");
  const fullCreative = (c.creative_style || "").toUpperCase() === "FULL_CREATIVE";
  // FULL CREATIVE (and legacy EVENT) full-canvas: the uploaded artwork IS the slide
  // (edge-to-edge, cover). FULL_CREATIVE suppresses title/subtitle/body entirely and
  // keeps a light bottom scrim only when a CTA/disclosure needs legibility.
  if ((fullCreative || isEvent) && c.image) {
    const hasCta = !!(c.cta_label || "").trim() && !!(c.cta_destination || "").trim();
    // PROTECTED MEDIA ZONE: the advertiser/editorial artwork carries ZERO PropertyConnect
    // overlays. Disclosure + CTA frame the media in a row BELOW it; carousel navigation
    // lives outside the slide entirely (in the Spotlight controls row).
    return (
      <div data-testid={`ad-hero-${c.id}`} className="flex flex-col">
        {/* PROTECTED MEDIA ZONE — advertiser artwork carries ZERO PropertyConnect overlays.
            Controlled adaptive geometry keeps portrait/ultrawide creatives from
            destabilizing the carousel; disclosure + CTA frame the media in a row below,
            and carousel navigation lives outside the slide (in the Spotlight controls). */}
        <AdaptiveMediaFrame
          src={c.image}
          alt={c.title || ""}
          policy="preservation"
          context="detail"
          maxHeight={360}
          rounded="rounded-2xl"
          className="border"
          style={{ borderColor: "rgba(212,160,23,0.3)" }}
          data-testid={`ad-hero-image-${c.id}`}
        />
        <div className="mt-2.5 flex items-center justify-between gap-3">
          <SponsorTag text={discLabel} />
          {hasCta && (
            <CTA c={c} placement={placement} className="text-[12px] px-3 py-1.5 sm:text-[13px] sm:px-4 sm:py-2" style={{ backgroundColor: GOLD, color: NAVY }} />
          )}
        </div>
      </div>
    );
  }
  return (
    <div data-testid={`ad-hero-${c.id}`} className="relative h-full overflow-hidden rounded-2xl border"
      style={{ background: "linear-gradient(160deg, #143163 0%, #0F2C59 55%, #0C2247 100%)", borderColor: "rgba(212,160,23,0.3)" }}>
      <div className="absolute inset-0 pointer-events-none" aria-hidden style={{ background: "radial-gradient(120% 120% at 100% 0%, rgba(212,160,23,0.2) 0%, rgba(212,160,23,0) 45%)" }} />
      <div className="relative z-10 p-5 sm:p-6 text-white flex flex-col h-full">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.1em]" style={{ background: "rgba(212,160,23,0.16)", color: GOLD, border: "1px solid rgba(212,160,23,0.4)" }}><Sparkles className="h-3.5 w-3.5" /> {c.campaign_type || "Spotlight"}</span>
          <SponsorTag text={discLabel} tone="light" />
        </div>
        {c.image ? <div className="mt-4 rounded-xl overflow-hidden border border-white/10 h-40"><img src={c.image} alt="" className="w-full h-full object-cover" /></div> : null}
        <h3 className="font-display text-xl sm:text-2xl font-bold leading-tight tracking-tight mt-4">{c.title}</h3>
        <div className="h-1 w-14 rounded-full mt-2.5" style={{ backgroundColor: GOLD }} aria-hidden />
        {c.subtitle ? <p className="text-[13.5px] font-semibold text-gold-300 mt-3" style={{ color: GOLD }}>{c.subtitle}</p> : null}
        {c.body ? <p className="text-[13.5px] leading-relaxed text-slate-200 mt-2">{c.body}</p> : null}
        <div className="mt-auto pt-5">
          <CTA c={c} placement={placement} className="justify-center text-[13.5px] px-4 py-2.5" style={{ backgroundColor: GOLD, color: NAVY }} />
        </div>
      </div>
    </div>
  );
}

/* ---- Variant: STRIP (marketplace/connections top horizontal banner) ---- */
function StripCard({ c, placement }) {
  useEffect(() => { track("impression", c, placement); }, [c, placement]);
  return (
    <div data-testid={`ad-strip-${c.id}`} className="relative overflow-hidden rounded-2xl border border-[#0F2C59]/10 bg-white shadow-sm flex flex-col sm:flex-row">
      <div className="sm:w-52 shrink-0 h-32 sm:h-auto bg-[#0F2C59]/5 relative">
        {c.image
          ? <img src={c.image} alt="" className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center"><Building2 className="h-10 w-10 text-[#0F2C59]/30" /></div>}
      </div>
      <div className="flex-1 p-4 sm:p-5 flex flex-col">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#0F2C59]">{c.campaign_type || "Spotlight"}</span>
          <SponsorTag text={c.disclosure} />
        </div>
        <h3 className="font-display text-lg font-semibold text-slate-900 mt-1.5 leading-snug">{c.title}</h3>
        {c.subtitle ? <p className="text-sm font-medium text-[#92600B] mt-0.5">{c.subtitle}</p> : null}
        {c.body ? <p className="text-[13px] text-slate-500 leading-relaxed mt-1 line-clamp-2">{c.body}</p> : null}
        <div className="mt-auto pt-3">
          <CTA c={c} placement={placement} className="text-[13px] px-3.5 py-2 text-accent-foreground" style={{ backgroundColor: GOLD, color: NAVY }} />
        </div>
      </div>
    </div>
  );
}

/* ---- Variant: MARKETPLACE GRID (3 equal image-dominant slots, creative-first) ----
   The uploaded artwork is the primary message. UI only frames it with a compact
   disclosure tag + optional CTA. FULL_CREATIVE never overlays title/copy; IMAGE_COPY
   adds a minimal title + CTA. 4:3 slots. >3 campaigns rotate coherently in sets of 3
   (deterministic priority order, no randomization) — the whole row swaps together. */
function AdSlotCard({ c, placement, slotIndex }) {
  useEffect(() => { track("impression", c, placement, slotIndex); }, [c, placement, slotIndex]);
  const fullCreative = (c.creative_style || "").toUpperCase() === "FULL_CREATIVE";
  const hasCta = !!(c.cta_label || "").trim() && !!(c.cta_destination || "").trim();
  const showCopy = !fullCreative && !!c.title;
  // Sponsored vs Featured — same semantics as Dashboard Spotlight (sponsor_kind).
  const isFeatured = (c.sponsor_kind || "").toUpperCase() === "FEATURED";
  const discLabel = isFeatured ? "Featured" : "Sponsored";
  return (
    <div data-testid={`ad-slot-${c.id}`} data-slot-index={slotIndex}
      className="overflow-hidden rounded-2xl border border-[#0F2C59]/10 bg-white shadow-sm flex flex-col">
      {/* PROTECTED MEDIA ZONE — fixed bounded stage (even card rhythm; placement policy
          caps page space). Contain (never crop text-heavy creative), centered, with a
          branded blurred backdrop. Zero PropertyConnect overlays. */}
      <AdaptiveMediaFrame
        src={c.image}
        alt={c.title || ""}
        policy="preservation"
        context="card"
        stageHeight="min(66vw, 320px)"
        rounded=""
        data-testid={`ad-slot-image-${c.id}`}
        fallback={<div className="w-full h-full flex items-center justify-center bg-[#0F2C59]/5"><Building2 className="h-10 w-10 text-[#0F2C59]/30" /></div>}
      />
      {/* Disclosure + campaign action framing the media, below it */}
      <div className="p-3 sm:p-3.5 flex flex-col gap-2">
        {showCopy && <h3 className="font-display text-[15px] sm:text-base font-bold leading-tight tracking-tight text-[#0F2C59] line-clamp-2">{c.title}</h3>}
        <div className="flex items-center justify-between gap-2">
          <SponsorTag text={discLabel} />
          {hasCta && (
            <CTA c={c} placement={placement} slotIndex={slotIndex} className="text-[12.5px] px-3.5 py-1.5" style={{ backgroundColor: GOLD, color: NAVY }} />
          )}
        </div>
      </div>
    </div>
  );
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(mq.matches);
    on(); mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return reduced;
}

function MarketplaceAdGrid({ campaigns, placement }) {
  const SET_SIZE = 3;
  const ROTATE_MS = 9000; // moderate cadence; whole 3-slot row swaps coherently
  const reduced = usePrefersReducedMotion();
  const numSets = Math.max(1, Math.ceil(campaigns.length / SET_SIZE));
  const [activeSet, setActiveSet] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (numSets <= 1 || paused || reduced) return;
    const t = setInterval(() => setActiveSet((s) => (s + 1) % numSets), ROTATE_MS);
    return () => clearInterval(t);
  }, [numSets, paused, reduced, activeSet]);

  const start = activeSet * SET_SIZE;
  const visible = campaigns.slice(start, start + SET_SIZE);

  return (
    <div data-testid="marketplace-ad-grid" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div key={activeSet} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${reduced ? "" : "animate-[pc-ad-fade_.5s_ease]"}`}>
        {visible.map((c, i) => (
          <AdSlotCard key={c.id} c={c} placement={placement} slotIndex={i + 1} />
        ))}
      </div>
      {numSets > 1 && (
        <div className="flex items-center justify-center gap-2 mt-3" data-testid="marketplace-ad-dots">
          {Array.from({ length: numSets }).map((_, i) => (
            <button key={i} data-testid={`marketplace-ad-dot-${i}`} aria-label={`Show sponsored set ${i + 1}`} aria-current={i === activeSet}
              onClick={() => setActiveSet(i)}
              className="h-1.5 rounded-full transition-all"
              style={{ width: i === activeSet ? 22 : 8, backgroundColor: i === activeSet ? GOLD : "rgba(15,44,89,0.2)" }} />
          ))}
        </div>
      )}
      <style>{`@keyframes pc-ad-fade{from{opacity:0}to{opacity:1}}`}</style>
    </div>
  );
}

/* ---- Variant: DEVELOPER STRIP (compact featured developer chips row) ---- */
function DeveloperStrip({ campaigns, placement }) {
  useEffect(() => { campaigns.forEach((c) => track("impression", c, placement)); }, [campaigns, placement]);
  return (
    <div data-testid="ad-developer-strip" className="rounded-2xl border border-[#0F2C59]/10 bg-[#0F2C59]/[0.03] p-3 sm:p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#0F2C59] flex items-center gap-1.5"><BadgeCheck className="h-4 w-4" style={{ color: GOLD }} /> Featured Developers</span>
        <SponsorTag text="Sponsored" />
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {campaigns.map((c) => (
          <Link key={c.id} to={c.cta_destination || "#"} data-testid={`ad-developer-${c.id}`} onClick={() => track("click", c, placement)}
            className="shrink-0 w-40 rounded-xl bg-white border border-slate-200 p-3 hover:shadow-md transition-shadow">
            <div className="h-14 rounded-lg bg-[#0F2C59]/5 flex items-center justify-center overflow-hidden mb-2">
              {c.image ? <img src={c.image} alt="" className="w-full h-full object-cover" /> : <Building2 className="h-6 w-6 text-[#0F2C59]/40" />}
            </div>
            <div className="text-[13px] font-semibold text-slate-900 truncate">{c.title}</div>
            {c.subtitle ? <div className="text-[11px] text-slate-500 truncate">{c.subtitle}</div> : null}
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ---- Variant: COMPACT (property financing partner panel) ---- */
function CompactPartner({ c, placement }) {
  useEffect(() => { track("impression", c, placement); }, [c, placement]);
  return (
    <div data-testid={`ad-compact-${c.id}`} className="rounded-2xl border border-[#0F2C59]/10 bg-[#0F2C59]/[0.03] p-4">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#0F2C59]">{c.campaign_type || "Partner"}</span>
        <SponsorTag text={c.disclosure} />
      </div>
      <div className="flex items-center gap-3">
        {c.image ? <img src={c.image} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" /> : null}
        <div className="min-w-0">
          <div className="text-[14px] font-semibold text-slate-900 leading-snug">{c.title}</div>
          {c.subtitle ? <div className="text-[12px] text-slate-500">{c.subtitle}</div> : null}
        </div>
      </div>
      <div className="mt-3">
        <CTA c={c} placement={placement} className="text-[12.5px] px-3 py-1.5 w-full justify-center" style={{ backgroundColor: GOLD, color: NAVY }} />
      </div>
    </div>
  );
}

/* ---- Public entry: renders the right variant for a placement, or nothing ---- */
export default function AdPlacement({ placement, variant = "strip", className = "" }) {
  const campaigns = useAdCampaigns(placement);
  if (!campaigns || campaigns.length === 0) return null;
  if (variant === "developer") {
    return <div className={className}><DeveloperStrip campaigns={campaigns} placement={placement} /></div>;
  }
  if (variant === "grid") {
    return <div className={className}><MarketplaceAdGrid campaigns={campaigns} placement={placement} /></div>;
  }
  const c = campaigns[0];
  if (variant === "compact") return <div className={className}><CompactPartner c={c} placement={placement} /></div>;
  return <div className={className}><StripCard c={c} placement={placement} /></div>;
}

export { useAdCampaigns, track as trackAdEvent };
